import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type {
  NestedDockmasterSpan,
  SpanEvent,
} from "../../../../../../../schemas";
import {
  useEventHighlightActions,
  useEventHighlightStore,
} from "../EventHighlightStore";

interface TimelineWithEventsProps {
  span: NestedDockmasterSpan;
  events: SpanEvent[];
}

export function TimelineWithEvents({ span, events }: TimelineWithEventsProps) {
  const { setHighlightedEventId } = useEventHighlightActions();
  const highlightedEventId = useEventHighlightStore(
    (state) => state.highlightedEventId,
  );
  const spanDuration =
    span.duration || (span.endTime ? span.endTime - span.startTime : 0);
  const spanStartTime = span.startTime;

  // Calculate event positions
  const eventPositions = events.map((event: SpanEvent, index: number) => {
    const eventTime = event.time || spanStartTime;
    const offsetFromStart = eventTime - spanStartTime;
    const percentage =
      spanDuration > 0 ? (offsetFromStart / spanDuration) * 100 : 0;

    return {
      index,
      percentage: Math.max(0, Math.min(100, percentage)),
      level:
        typeof event.attributes?.level === "string"
          ? event.attributes?.level
          : "info",
      message:
        typeof event.attributes?.message === "string"
          ? event.attributes?.message
          : event.name || "Event",
      time: eventTime,
    };
  });

  return (
    <div className="relative">
      {/* Timeline container inspired by the provided design */}
      {/* Main layout: Operation name on left, timeline in center, duration on right */}
      <div className="grid grid-cols-[1fr_40px] items-center gap-3">
        {/* Timeline bar container - takes remaining space */}
        <div className="flex-1 relative">
          {/* Timeline container with vertical end markers */}
          <div className="relative flex items-center h-2">
            {/* Start marker */}
            <div className="w-0.5 h-2 bg-primary rounded-full shrink-0" />

            {/* Orange timeline bar */}
            <div className="absolute h-[10px] bg-primary/50 transition-all duration-300 w-full" />

            {/* Event dots positioned along the timeline - using absolute positioning to not affect height */}
            {eventPositions.map((eventPos) => {
              const eventId = `${span.spanId}-event-${eventPos.index}`;
              const isHighlighted = highlightedEventId === eventId;

              return (
                <div
                  key={eventPos.index}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20"
                  style={{ left: `${eventPos.percentage}%` }}
                  onMouseEnter={() => setHighlightedEventId(eventId)}
                  onMouseLeave={() => setHighlightedEventId(null)}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full border cursor-pointer transition-all duration-200 shadow-sm",
                          "bg-background border-primary",
                          isHighlighted && "w-2.5 h-2.5 bg-primary",
                        )}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="font-medium">{eventPos.message}</div>
                      <div className="text-muted-foreground">
                        {eventPos.time
                          ? new Date(eventPos.time).toLocaleTimeString()
                          : "Unknown time"}
                      </div>
                      {eventPos.level !== "info" && (
                        <div className="text-xs opacity-75">
                          Level: {eventPos.level}
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
            {/* </div> */}

            {/* End marker */}
            <div className="w-0.5 h-2 bg-primary rounded-full shrink-0 absolute right-0" />
          </div>
        </div>

        {/* Duration display on the right */}
        <div className="text-xs text-muted-foreground font-mono shrink-0">
          {formatTimelineLabel(spanDuration)}
        </div>
      </div>
    </div>
  );
}

function formatTimelineLabel(duration: number): string {
  if (duration < 1) {
    return "<1ms";
  }
  if (duration < 1000) {
    return `${Math.round(duration)}ms`;
  }
  return `${(duration / 1000).toFixed(1)}s`;
}
